<template>
  <div class="facture-apercu-cadre" ref="cadre">
    <div class="facture-feuille"
         :class="'facture-feuille--' + facture.template"
         :style="{transform: 'scale(' + echelle + ')'}"
         ref="feuille">

      <!-- En-tête : le logo porte l'identité, les coordonnées sont en pied -->
      <div class="feuille-entete">
        <div class="feuille-entete-gauche">
          <img v-if="facture.options.logo && logoDataUrl"
               class="feuille-logo"
               :src="logoDataUrl">
          <div v-else class="feuille-emetteur-nom">
            {{ facture.emetteur.nom || 'Votre dénomination' }}
          </div>
        </div>
        <div class="feuille-entete-droite">
          <div class="feuille-titre" :style="{color: couleur}">FACTURE</div>
          <table class="feuille-meta">
            <tr>
              <td>Numéro</td>
              <th>{{ facture.numero || '—' }}</th>
            </tr>
            <tr>
              <td>Émise le</td>
              <th>{{ facture.dateEmission }}</th>
            </tr>
            <tr v-if="facture.options.echeance && facture.dateEcheance">
              <td>Échéance</td>
              <th>{{ facture.dateEcheance }}</th>
            </tr>
            <tr v-if="facture.options.reference && facture.reference">
              <td>Référence</td>
              <th>{{ facture.reference }}</th>
            </tr>
          </table>
        </div>
      </div>

      <!-- Destinataire -->
      <div class="feuille-client">
        <div class="feuille-client-nom">{{ facture.client.nom || 'Nom du client' }}</div>
        <div class="feuille-client-lignes">
          <div v-if="facture.client.adresse">{{ facture.client.adresse }}</div>
          <div v-if="facture.client.cp || facture.client.ville">
            {{ facture.client.cp }} {{ facture.client.ville }}
          </div>
          <div v-if="facture.client.pays">{{ facture.client.pays }}</div>
          <div v-if="facture.client.email">{{ facture.client.email }}</div>
          <div v-if="facture.client.numEntreprise">N° TVA : {{ facture.client.numEntreprise }}</div>
        </div>
      </div>

      <!-- Lignes -->
      <table class="feuille-lignes">
        <thead>
          <tr :style="styleEnteteTableau">
            <th class="col-description">Description</th>
            <th class="col-nombre">Qté</th>
            <th class="col-nombre">Prix</th>
            <th v-if="colonneTva" class="col-nombre">% TVA</th>
            <th class="col-nombre">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ligne, index) in facture.lignes" :key="ligne.id">
            <td class="col-description">{{ ligne.description }}</td>
            <td class="col-nombre">{{ ligne.quantite }}</td>
            <td class="col-nombre">{{ fm(ligne.prixUnitaire) }}</td>
            <td v-if="colonneTva" class="col-nombre">{{ tauxDeLigne(ligne) }} %</td>
            <td class="col-nombre">{{ fm(totaux.montantsLignes[index]) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Totaux -->
      <div class="feuille-totaux">
        <table>
          <tr v-if="afficheDetailTotaux">
            <td>Sous-total</td>
            <td class="col-nombre">{{ fm(totaux.sousTotal) }}</td>
          </tr>
          <tr v-if="facture.options.remise">
            <td>Remise{{ facture.remise.type === 'pourcentage' ? ' (' + facture.remise.valeur + ' %)' : '' }}</td>
            <td class="col-nombre">- {{ fm(totaux.montantRemise) }}</td>
          </tr>
          <tr v-if="facture.options.tva">
            <td>TVA{{ facture.tva.mode === 'global' ? ' (' + facture.tva.taux + ' %)' : '' }}</td>
            <td class="col-nombre">{{ fm(totaux.montantTva) }}</td>
          </tr>
          <tr v-if="facture.options.frais">
            <td>Frais</td>
            <td class="col-nombre">{{ fm(totaux.frais) }}</td>
          </tr>
          <tr class="feuille-total" :style="styleTotal">
            <td>Total</td>
            <td class="col-nombre">{{ fm(totaux.total) }}</td>
          </tr>
        </table>
      </div>

      <div class="feuille-mentions">
        <div v-if="facture.options.tva && facture.tva.mentionExoneration" class="feuille-mention">
          {{ facture.tva.mentionExoneration }}
        </div>

        <div v-if="(facture.options.paiement && blocPaiementRempli) || (facture.options.qr && qrDataUrl)"
             class="feuille-paiement-bloc"
             :style="{borderLeftColor: couleur}">
          <div class="feuille-paiement">
            <div class="feuille-sous-titre" :style="{color: couleur}">PAIEMENT</div>
            <div v-if="facture.paiement.beneficiaire">{{ facture.paiement.beneficiaire }}</div>
            <div v-if="facture.paiement.iban">IBAN : {{ facture.paiement.iban }}</div>
            <div v-if="facture.paiement.bic">BIC : {{ facture.paiement.bic }}</div>
            <div v-if="facture.paiement.communicationStructuree">
              Communication : {{ facture.paiement.communicationStructuree }}
            </div>
          </div>
          <div v-if="facture.options.qr && qrDataUrl" class="feuille-qr-cellule">
            <img class="feuille-qr" :src="qrDataUrl">
            <div class="feuille-qr-legende">Scannez pour payer</div>
          </div>
        </div>

        <div v-if="facture.options.conditions && facture.conditionsPaiement" class="feuille-bloc-texte">
          <div class="feuille-sous-titre" :style="{color: couleur}">CONDITIONS</div>
          <div class="feuille-texte-libre">{{ facture.conditionsPaiement }}</div>
        </div>

        <div v-if="facture.options.notes && facture.notes" class="feuille-bloc-texte">
          <div class="feuille-sous-titre" :style="{color: couleur}">NOTES</div>
          <div class="feuille-texte-libre">{{ facture.notes }}</div>
        </div>
      </div>

      <!-- Coordonnées complètes de l'émetteur, en pied de page -->
      <div class="feuille-pied">
        <div v-for="(ligne, index) in lignesPied" :key="index">{{ ligne }}</div>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import {formaterMontant} from '../../../utils/facture/calculs'
  import {COULEUR_ACCENT} from '../../../utils/facture/modele'
  import {lignesPiedEmetteur} from '../../../utils/facture/pdf'

  // Dimensions A4 à 96 dpi : la feuille est rendue à taille réelle puis mise à
  // l'échelle, pour que l'aperçu ne mente pas sur ce que produira pdfmake.
  const LARGEUR_A4 = 794
  const HAUTEUR_A4 = 1123

  export default {
    name: 'FactureApercu',
    props: {
      facture: {type: Object, required: true},
      totaux: {type: Object, required: true},
      qrDataUrl: {type: String, default: null},
      logoDataUrl: {type: String, default: null}
    },
    data () {
      return {
        echelle: 1
      }
    },
    computed: {
      couleur () {
        return COULEUR_ACCENT
      },
      // Même règle que le PDF : la colonne apparaît dès que la TVA s'applique
      colonneTva () {
        return this.facture.options.tva
      },
      afficheDetailTotaux () {
        return this.facture.options.tva || this.facture.options.remise || this.facture.options.frais
      },
      blocPaiementRempli () {
        const paiement = this.facture.paiement
        return !!(paiement.beneficiaire || paiement.iban || paiement.bic ||
          paiement.communicationStructuree)
      },
      // Mêmes lignes que le pied du PDF, construites par le même code
      lignesPied () {
        return lignesPiedEmetteur(this.facture.emetteur)
      },
      styleEnteteTableau () {
        if (this.facture.template === 'minimal') {
          return {borderBottomColor: this.couleur, color: this.couleur}
        }
        return {backgroundColor: this.couleur, color: '#ffffff'}
      },
      styleTotal () {
        return {borderTopColor: this.couleur, color: this.couleur}
      }
    },
    mounted () {
      this.ajusterEchelle()
      window.addEventListener('resize', this.ajusterEchelle)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.ajusterEchelle)
    },
    updated () {
      this.ajusterEchelle()
    },
    methods: {
      fm (valeur) {
        return formaterMontant(valeur, this.facture.devise)
      },
      tauxDeLigne (ligne) {
        return this.facture.tva.mode === 'ligne' ? ligne.tauxTva : this.facture.tva.taux
      },
      ajusterEchelle () {
        const cadre = this.$refs.cadre
        if (!cadre) return
        const largeur = cadre.clientWidth
        if (!largeur) return
        this.echelle = Math.min(1, largeur / LARGEUR_A4)
        // Le conteneur reprend la hauteur réellement occupée après mise à l'échelle
        const feuille = this.$refs.feuille
        const hauteur = feuille ? feuille.scrollHeight : HAUTEUR_A4
        cadre.style.height = (Math.max(hauteur, HAUTEUR_A4) * this.echelle) + 'px'
      }
    }
  }
</script>

<style scoped>
  .facture-apercu-cadre {
    overflow: hidden;
    position: relative;
    /* La feuille mise à l'échelle ne dicte pas la largeur du cadre : sans
       cette règle il se dimensionne sur son contenu et l'aperçu reste étroit */
    width: 100%;
  }

  .facture-feuille {
    position: relative;
    width: 794px;
    min-height: 1123px;
    padding: 53px 53px 110px 53px;
    background: #fff;
    color: #333;
    font-family: Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 13px;
    line-height: 1.45;
    box-shadow: 0 0 6px rgba(0, 0, 0, .25);
    transform-origin: top left;
    box-sizing: border-box;
  }

  .facture-feuille--moderne {
    border-top: 8px solid #3c8dbc;
  }

  .feuille-entete {
    display: table;
    width: 100%;
    margin-bottom: 30px;
  }

  .facture-feuille--classique .feuille-entete {
    border-bottom: 1px solid #ddd;
    padding-bottom: 18px;
  }

  .feuille-entete-gauche,
  .feuille-entete-droite {
    display: table-cell;
    vertical-align: top;
  }

  .feuille-entete-droite {
    text-align: right;
    width: 42%;
  }

  .feuille-logo {
    max-height: 82px;
    max-width: 265px;
  }

  .feuille-emetteur-nom {
    font-size: 17px;
    font-weight: 700;
  }

  .feuille-titre {
    font-size: 30px;
    font-weight: 700;
    letter-spacing: 2px;
    margin-bottom: 10px;
  }

  .facture-feuille--classique .feuille-titre {
    letter-spacing: 6px;
    font-weight: 400;
  }

  .facture-feuille--minimal .feuille-titre {
    font-size: 22px;
    font-weight: 300;
    letter-spacing: 4px;
  }

  .feuille-meta {
    margin-left: auto;
    font-size: 12px;
  }

  .feuille-meta td {
    color: #888;
    padding: 1px 10px 1px 0;
    text-align: right;
  }

  .feuille-meta th {
    text-align: right;
    font-weight: 700;
  }

  .feuille-client {
    margin-bottom: 26px;
  }

  .feuille-client-nom {
    font-size: 14px;
    font-weight: 700;
  }

  .feuille-client-lignes {
    font-size: 12px;
    color: #777;
    margin-top: 3px;
  }

  .feuille-lignes {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .feuille-lignes th {
    padding: 7px 8px;
    text-align: left;
    font-weight: 700;
  }

  .feuille-lignes td {
    padding: 7px 8px;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  .facture-feuille--classique .feuille-lignes th,
  .facture-feuille--classique .feuille-lignes td {
    border: 1px solid #ddd;
  }

  .facture-feuille--minimal .feuille-lignes th {
    background: transparent !important;
    border-bottom: 2px solid #3c8dbc;
    padding-left: 0;
  }

  .facture-feuille--minimal .feuille-lignes td {
    padding-left: 0;
    border-bottom: 1px solid #f2f2f2;
  }

  .col-nombre {
    text-align: right;
    white-space: nowrap;
  }

  .col-description {
    white-space: pre-wrap;
  }

  .feuille-totaux {
    margin-top: 16px;
    overflow: hidden;
  }

  .feuille-totaux table {
    float: right;
    min-width: 260px;
    font-size: 12px;
  }

  .feuille-totaux td {
    padding: 4px 0 4px 18px;
  }

  .feuille-totaux td:first-child {
    color: #777;
  }

  .feuille-total td {
    border-top: 2px solid #3c8dbc;
    font-size: 15px;
    font-weight: 700;
    padding-top: 8px;
  }

  .feuille-mentions {
    clear: both;
    padding-top: 30px;
    font-size: 11px;
    color: #555;
  }

  .feuille-sous-titre {
    font-size: 11px;
    letter-spacing: 1px;
    font-weight: 700;
    margin-bottom: 3px;
  }

  .feuille-mention {
    font-style: italic;
    margin-bottom: 14px;
  }

  /* Panneau teinté avec filet d'accent : le QR est tenu dans le bloc de
     paiement, pas posé au milieu de la page */
  .feuille-paiement-bloc {
    display: table;
    width: 100%;
    margin-bottom: 16px;
    padding: 14px 16px;
    background: #f4f8fb;
    border-left: 4px solid #3c8dbc;
    box-sizing: border-box;
  }

  .feuille-paiement {
    display: table-cell;
    vertical-align: top;
    font-size: 12px;
  }

  .feuille-qr-cellule {
    display: table-cell;
    vertical-align: top;
    width: 104px;
    text-align: center;
  }

  .feuille-qr {
    width: 104px;
    height: 104px;
  }

  .feuille-qr-legende {
    font-size: 9px;
    color: #777;
    margin-top: 4px;
  }

  .feuille-bloc-texte {
    padding-top: 14px;
  }

  .feuille-texte-libre {
    white-space: pre-wrap;
  }

  .feuille-pied {
    position: absolute;
    left: 53px;
    right: 53px;
    bottom: 40px;
    padding-top: 10px;
    border-top: 1px solid #ddd;
    text-align: center;
    font-size: 10px;
    color: #777;
    line-height: 1.6;
  }
</style>



// WEBPACK FOOTER //
// src/components/Outils/Facture/FactureApercu.vue